import {
    StepperDropdownContainer,
    StepperDropdownContent,
    StepperDropdownHeader,
    StepperDropdownHeaderIcon,
    StepperDropdownHeaderTitle
} from '../../dropdown/StepperDropdown'
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { StepperProps } from '../../../utils/stepper-types'
import { InfoSvg, StepCompletedSvg } from '../../../../../CommonComponents/svgs/Svgs'
import DatetimeInput from '../../date-time-input/DatetimeInput'
import useUserData from '../../../hooks/useUserData'
import useForm from '../../../hooks/useForm'
import Select from '../../../../../CommonComponents/select/Select'
import { checkCommonNameValidation, showToastMessage } from '../../../../../../utils/commonFunctions'
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../../constants'
import useViewer from '../../../hooks/useViewer'
import useNavigation from '../../../hooks/useNavigation'
import { showHideDataSourceByName, requestRender } from '../../../../../../utils/cesiumHelperFunctions'
import { clearViewer, plotScenarioElements } from '../../../utils/viewer-packets'
import { DATA_SOURCE_NAMES } from '../../../utils/constants'
import MissionService from '../../../../../../service/MissionService'


const OperationTypes = {
    'ConOps': 'CONOPS',
    'CONOPS': 'ConOps',
    'LEOP': 'LEOP',
    'Manual': 'Manual'
}

const ScenarioModes = {
    'SWIL': 'SIMULATE',
    'SIMULATE': 'SWIL',
    'Remote HWIL': 'HARDWARE_IN_LOOP',
    'HARDWARE_IN_LOOP': 'Remote HWIL',
    'TRUETWIN_COMMAND_CENTER': 'Open Scenario',
    'Open Scenario': 'TRUETWIN_COMMAND_CENTER'
}

const ElementTypeDisplayNames: Record<string, string> = {
    'groundstation': 'Ground Station',
}

export const formatElementType = (type: string): string => {
    const lower = type.toLowerCase()
    if (ElementTypeDisplayNames[lower]) return ElementTypeDisplayNames[lower]
    return type.charAt(0).toUpperCase() + type.slice(1)
}


const ScenarioDetails: FC<StepperProps> = ({ title, hideComponent, open, disabled, stepCompleted, onToggle }) => {
    const { userData } = useUserData()

    const { formData, setFormData, scenarioJson, setScenarioJson, simulatorList, setSimulatorList, selectedSimulator, setSelectedSimulator } = useForm()
    const { viewer, updateGroundPackets } = useViewer()
    const { missionId } = useNavigation()


    const scenarioName = useMemo(() => formData.details.scenarioName, [formData.details.scenarioName])
    const scenarioOperationType = useMemo(() => formData.details.scenarioOperation, [formData.details.scenarioOperation])
    const scenarioDeploymentType = useMemo(() => formData.details.scenarioDeploymentType, [formData.details.scenarioDeploymentType])
    const scenarioStartTime = useMemo(() => formData.details.scenarioStartTime, [formData.details.scenarioStartTime])
    const sampleRate = useMemo(() => formData.details.sampleRate, [formData.details.sampleRate])

    const openScenario = useMemo(() => scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER, [scenarioDeploymentType])

    const showScaleFactor = true //disabling this for now
    const showHwilInfo = useMemo(() => scenarioDeploymentType === 'HARDWARE_IN_LOOP', [scenarioDeploymentType])

    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const prevOperationRef = useRef(scenarioOperationType)

    // Lazy fetch simulator list — only when Manual operation type is selected
    useEffect(() => {
        if (scenarioOperationType === 'Manual' && missionId && simulatorList.length === 0) {
            MissionService.getSimulatorList(missionId)
                .then((res: any) => {
                    if (res?.data?.data) {
                        setSimulatorList(res.data.data)
                    }
                })
                .catch((err: any) => {
                    console.error('Error fetching simulator list:', err)
                })
        }
    }, [scenarioOperationType, missionId])

    const onChangeFormData = (field: string, value: string | number) => {
        setFormData((prev) => ({
            ...prev,
            details: {
                ...prev.details,
                [field]: value
            }
        }))
    }

    const onChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!checkCommonNameValidation(event.target.value, 26)) return
        onChangeFormData('scenarioName', event.target.value.trimStart())
    }

    const onSelectChange = (field: string, value: string) => {
        if (field === 'scenarioOperation') {
            setFormData((prev) => ({
                ...prev,
                details: {
                    ...prev.details,
                    [field]: value,
                    scenarioDeploymentType: value === 'LEOP' ? 'SIMULATE' : prev.details.scenarioDeploymentType
                }
            }))
        } else {
            onChangeFormData(field, value)
        }
    }

    const onChangeScaleFactor = (mode: string) => {
        const value = mode === 'increase' ? sampleRate + 1 : sampleRate - 1
        onChangeFormData('sampleRate', value)
    }


    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return

        if (file.type !== 'application/json') {
            showToastMessage('info', 'Please upload a valid JSON file')
            return
        }

        const reader = new FileReader()

        reader.onload = (e) => {
            try {
                const jsonData = JSON.parse(e.target?.result as string)
                setUploadedFileName(file.name)
                setScenarioJson(jsonData)
                setSelectedSimulator({})
                if (simulatorList.length === 0) {
                    showToastMessage('info', 'No simulators available. Please add one to continue.')
                }
            } catch (error) {
                showToastMessage('error', 'Invalid JSON file')
            }
        }

        reader.readAsText(file)
    }


    const uploadJsonFile = () => {
        fileInputRef.current?.click()
    }

    const removeUploadedFile = () => {
        setUploadedFileName(null)
        setScenarioJson(null)
        setSelectedSimulator({})
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
        clearViewer(viewer)
    }




    useEffect(() => {
        if (!viewer) return
        const isManual = scenarioOperationType === 'Manual'
        const wasManual = prevOperationRef.current === 'Manual'

        if (isManual && !wasManual) {
            // Switching TO Manual — hide existing data sources and re-plot manual entities if JSON exists
            DATA_SOURCE_NAMES.forEach((name) => {
                showHideDataSourceByName(viewer, name, false)
            })
            if (scenarioJson) {
                plotScenarioElements(viewer, scenarioJson)
            }
        } else if (!isManual && wasManual) {
            // Switching FROM Manual — clear manual entities and restore existing ones
            clearViewer(viewer)
            updateGroundPackets(formData)
        }

        prevOperationRef.current = scenarioOperationType
        requestRender(viewer)
    }, [scenarioOperationType, viewer])



    if (hideComponent) return null
    return (
        <StepperDropdownContainer
            open={open}
            disabled={disabled}
        >
            <StepperDropdownHeader
                title={title}
                onToggle={onToggle}
            >
                <StepperDropdownHeaderTitle>
                    {title}
                </StepperDropdownHeaderTitle>
                <StepperDropdownHeaderIcon show={stepCompleted}>
                    <StepCompletedSvg />
                </StepperDropdownHeaderIcon>
            </StepperDropdownHeader>
            <StepperDropdownContent>
                <div className='form__input__container'>
                    <span className='form__input__label'>Title</span>
                    <div className='form__field__container'>
                        <input type='text' autoFocus value={scenarioName} placeholder='Scenario Title' className='form__input' onChange={onChangeName} />
                    </div>
                </div>

                {openScenario &&
                    <div className='form__input__container'>
                        <span className='form__input__label'>Start Time</span>
                        <div className='form__field__container'>
                            <DatetimeInput onChange={(value) => {
                                onChangeFormData('scenarioStartTime', value)
                            }} value={scenarioStartTime} key={'scenarioStartTime'} />
                        </div>
                    </div>
                }

                {!openScenario &&
                    <Fragment>
                        <div className='form__input__container'>
                            <span className='form__input__label'>Operation</span>
                            <div className='form__field__container'>
                                <Select id={1} placement="bottom" value={OperationTypes[scenarioOperationType]}
                                    onSelect={(value) => { onSelectChange('scenarioOperation', OperationTypes[value as string]) }}>
                                    <Select.Trigger>
                                        <Select.Value placeholder="Select Operation" />
                                    </Select.Trigger>
                                    <Select.Content>
                                        <Select.Group>
                                            <Select.Item value="ConOps">ConOps</Select.Item>
                                            <Select.Item value="LEOP">LEOP</Select.Item>
                                            <Select.Item value="Manual">Manual</Select.Item>
                                        </Select.Group>
                                    </Select.Content>
                                </Select>
                            </div>
                        </div>
                        {scenarioOperationType !== 'Manual' ?
                            <>
                                <div className={`form__input__container ${scenarioOperationType === 'LEOP' && 'disabled'}`}>
                                    <span className='form__input__label'>Mode</span>
                                    <div className='form__field__container'>
                                        <Select id={2} placement="bottom" value={ScenarioModes[scenarioDeploymentType]}
                                            onSelect={(value) => { onSelectChange('scenarioDeploymentType', ScenarioModes[value as string]) }}>
                                            <Select.Trigger disabled={scenarioOperationType === 'LEOP'}>
                                                <Select.Value placeholder="Select Mode" />
                                            </Select.Trigger>
                                            <Select.Content>
                                                <Select.Group>
                                                    <Select.Item value="SWIL">SWIL</Select.Item>
                                                    <Select.Item value="Remote HWIL">Remote HWIL</Select.Item>
                                                </Select.Group>
                                            </Select.Content>
                                        </Select>
                                    </div>
                                </div>
                                {showHwilInfo &&
                                    <div className='information__container'>
                                        <InfoSvg />
                                        <span className='information__text'>Hybrid simulation mode lets you connect some devices remotely for simulation.</span>
                                    </div>
                                }
                                <div className='form__input__container'>
                                    <span className='form__input__label'>Start Time</span>
                                    <div className='form__field__container'>
                                        <DatetimeInput onChange={(value) => {
                                            onChangeFormData('scenarioStartTime', value)
                                        }} value={scenarioStartTime} key={'scenarioStartTime'} />
                                    </div>
                                </div>
                                {showScaleFactor &&
                                    <div className={`time__scaling__factor__container opacity-50`}>
                                        <span className="time__scaling__factor__label">Time Scaling Factor</span>
                                        <div className="time__scaling__factor__input__container">
                                            <button
                                                className="time__scaling__factor__button minus_button"
                                                onClick={() => onChangeScaleFactor('decrease')}
                                                disabled={true}
                                            >
                                                -
                                            </button>
                                            <input
                                                disabled
                                                value={`${sampleRate}X`}
                                                className="time__scaling__factor__input"
                                                type="text"
                                            />
                                            <button
                                                className="time__scaling__factor__button plus_button"
                                                onClick={() => onChangeScaleFactor('increase')}
                                                disabled={true}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                }
                            </> :
                            <>
                                <div className='form__input__container'>
                                    <span className='form__input__label'>Scenario Config</span>
                                </div>

                                <div className='form__input__container'>
                                    {!uploadedFileName ? (
                                        <>
                                            <input
                                                type="file"
                                                accept=".json"
                                                ref={fileInputRef}
                                                style={{ display: 'none' }}
                                                onChange={onFileChange}
                                            />
                                            <div className='upload_file_element' onClick={uploadJsonFile}>
                                                Upload File
                                            </div>
                                        </>
                                    ) : (
                                        <div className='uploaded_file'>
                                            <span >{uploadedFileName}</span>
                                            <span onClick={removeUploadedFile}>
                                                ✕
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {scenarioJson && (() => {
                                    const elementTypes = Array.from(new Set(
                                        scenarioJson.elements?.map((el: any) => el?.element_config?.element_type).filter(Boolean)
                                    )) as string[]

                                    return elementTypes.map((elementType, index) => (
                                        <div className='form__input__container' key={elementType}>
                                            <span className='form__input__label'>{formatElementType(elementType)}</span>
                                            <div className='form__field__container'>
                                                <Select
                                                    id={100 + index}
                                                    placement="bottom"
                                                    value={selectedSimulator[elementType] || ''}
                                                    onSelect={(simulatorName) => {
                                                        const sim = simulatorList.find(s => s.simulatorName === simulatorName)
                                                        const simulatorId = sim?.simulatorId || ''

                                                        setSelectedSimulator(prev => ({
                                                            ...prev,
                                                            [elementType]: simulatorName as string
                                                        }))

                                                        setScenarioJson(prev => {
                                                            if (!prev) return prev
                                                            const updatedElements = prev.elements.map((el: any) => {
                                                                if (el?.element_config?.element_type === elementType) {
                                                                    return { ...el, simulator_id: simulatorId }
                                                                }
                                                                return el
                                                            })
                                                            return {
                                                                ...prev,
                                                                elements: updatedElements
                                                            }
                                                        })
                                                    }}
                                                >
                                                    <Select.Trigger disabled={simulatorList.length === 0}>
                                                        <Select.Value
                                                            placeholder={
                                                                !simulatorList?.length
                                                                    ? 'No simulators available'
                                                                    : 'Select simulator'
                                                            }
                                                        />
                                                    </Select.Trigger>
                                                    <Select.Content>
                                                        <Select.Group>
                                                            {simulatorList.map((simulator) => (
                                                                <Select.Item key={simulator.simulatorId} value={simulator.simulatorName}>
                                                                    {simulator.simulatorName}
                                                                </Select.Item>
                                                            ))}
                                                        </Select.Group>
                                                    </Select.Content>
                                                </Select>
                                            </div>
                                        </div>
                                    ))
                                })()}
                            </>
                        }
                    </Fragment>
                }
            </StepperDropdownContent>
        </StepperDropdownContainer>
    )
}

export default ScenarioDetails